import { useCallback, useContext, useRef, useState } from 'react';
import { AuthContext } from '../Components/context/auth-context';

// Per-user localStorage key prefix for remembered UI settings.
const STORAGE_KEY_PREFIX = 'fieldtime_pref_';

export const getPreferenceStorageKey = (userId, key) =>
  `${STORAGE_KEY_PREFIX}${userId || 'anonymous'}_${key}`;

const readStored = (storageKey, defaultValue) => {
  try {
    const raw = localStorage.getItem(storageKey);
    return raw === null ? defaultValue : JSON.parse(raw);
  } catch {
    return defaultValue;
  }
};

/**
 * Remembers a UI setting per user (e.g. a dialog's last-used options).
 *
 * Release 1 stores to localStorage; when the per-user preferences table exists
 * only this hook changes, not its callers. Storage errors (private mode, quota,
 * bad JSON) are ignored and the default is used.
 *
 * Changing `key` (e.g. switching project) loads that key's stored value.
 *
 * @param {string} key          e.g. 'packageExport.' + projectId
 * @param {*} defaultValue      returned when nothing is stored
 * @returns {[any, Function, Function]} [value, setValue, reset]
 *   setValue accepts a value or an updater function, like useState.
 *   reset removes the stored value and returns to the default.
 */
export const usePreference = (key, defaultValue) => {
  const { userId } = useContext(AuthContext);
  const storageKey = getPreferenceStorageKey(userId, key);

  const defaultRef = useRef(defaultValue);
  defaultRef.current = defaultValue;

  const [state, setState] = useState(() => ({
    storageKey,
    value: readStored(storageKey, defaultValue),
  }));

  // Key changed: load the new key's value during render (no stale flash).
  let current = state;
  if (state.storageKey !== storageKey) {
    current = { storageKey, value: readStored(storageKey, defaultValue) };
    setState(current);
  }

  const valueRef = useRef(current.value);
  valueRef.current = current.value;

  const setValue = useCallback(
    (next) => {
      const resolved =
        typeof next === 'function' ? next(valueRef.current) : next;
      valueRef.current = resolved;
      try {
        localStorage.setItem(storageKey, JSON.stringify(resolved));
      } catch {
        // Ignore quota / serialization errors — the in-memory value still updates.
      }
      setState({ storageKey, value: resolved });
    },
    [storageKey]
  );

  const reset = useCallback(() => {
    try {
      localStorage.removeItem(storageKey);
    } catch {
      // Ignore storage errors.
    }
    valueRef.current = defaultRef.current;
    setState({ storageKey, value: defaultRef.current });
  }, [storageKey]);

  return [current.value, setValue, reset];
};

export default usePreference;
